import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageAction, PageActionEvent } from '../../interfaces/page-action.interface';

/**
 * Mediator service for page actions.
 * Used by HeaderComponent to dispatch actions and by page components to handle them.
 */
@Injectable({ providedIn: 'root' })
export class PageActionService {
  private actionTriggered$ = new Subject<PageActionEvent>();
  private currentActions$ = new BehaviorSubject<PageAction[]>([]);

  /** Observable of current page actions */
  get actions(): Observable<PageAction[]> {
    return this.currentActions$.asObservable();
  }

  /** Dispatch an action event (called by HeaderComponent) */
  dispatch(event: PageActionEvent): void {
    this.actionTriggered$.next(event);
  }

  /** Set page actions (called on route change) */
  setActions(actions: PageAction[]): void {
    this.currentActions$.next([...actions]);
  }

  /**
   * Subscribe to actions with auto-cleanup.
   * 
   * @example
   * private destroy$ = new Subject<void>();
   * 
   * ngOnInit() {
   *   this.pageActionService.handleActionsUntil(this.destroy$, {
   *     save: () => this.onSave(),
   *     delete: () => this.onDelete(),
   *   });
   * }
   * 
   * ngOnDestroy() {
   *   this.destroy$.next();
   *   this.destroy$.complete();
   * }
   */
  handleActionsUntil(
    destroy$: Observable<void>,
    handlers: Record<string, (event: PageActionEvent) => void>
  ): void {
    this.actionTriggered$.pipe(takeUntil(destroy$)).subscribe(event => {
      const handler = handlers[event.action.id];
      if (handler) handler(event);
    });
  }
}
